import 'mapbox-gl/dist/mapbox-gl.css';

import mapboxgl from 'mapbox-gl'; // This is a dependency of react-map-gl even if you didn't explicitly install it
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Map, { Marker, Popup } from 'react-map-gl';

import cls from '@soluto-design/styles/cls';

import { Text, Title } from '@soluto-design/react';

import { FormProps } from '../../molecules/Form';

import { CalculatorFormValues } from './components/Form';
import Geocoder from './components/Geocoder';
import Pin from './components/Pin';
import ProgressBar from './components/ProgressBar';
import StepsContent from './components/StepsContent';

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass =
    require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const MAPBOX_STYLES = 'mapbox://styles/bogdanvectuel/cjfwdik375q302sn2jn9mkr8e';

const DEFAULT_MAP = {
    latitude: -27.262781500000003,
    longitude: -70.776655,
    zoom: 8,
};

type PardotSubmitProps = {
    first_name: string;
    last_name: string;
    email: string;
};

export interface CalculatorSectionProps {
    title: string;
    description: string;
    calculatorTitle: string;
    calculatorDescription: string;
    formTitle: string;
    formDescription: string;
    submitTitle: string;
    submitDescription: string;
    benefits: any[];
    caption: string;
    backButton: any;
    form: FormProps;
    onSubmit: (data: any) => void;
    mapToken: string;
    translations?: {
        [key: string]: {
            text: string;
        };
    };
}

const CalculatorSection: React.FC<CalculatorSectionProps> = ({
    title,
    description,
    calculatorTitle,
    calculatorDescription,
    submitTitle,
    submitDescription,
    formTitle,
    formDescription,
    benefits,
    caption,
    backButton,
    form,
    onSubmit,
    mapToken,
    translations = {},
}) => {
    const [step, setSteps] = useState<number>(0);
    const [resultCalculator, setResult] = useState<any>();
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const geocoderRef = useRef<any>(null);

    const methods = useForm<CalculatorFormValues>({
        mode: 'onSubmit',
        shouldUnregister: false,
        defaultValues: {
            structureType: 'sat',
            peakCapacity: 50,
            specificCapex: 1080,
            specificOpex: 10,
            returnRate: 7,
        },
    });

    const [marker, setMarker] = useState(null);

    const nextStep = useCallback(() => {
        setSteps((prevStep: number) => prevStep + 1);
    }, []);

    const previousStep = useCallback(() => {
        setSteps((prevStep: number) => prevStep - 1);
    }, []);

    const resetSteps = useCallback(() => {
        geocoderRef.current?._typeahead?.list?.draw();
        setMarker(null);
        setSteps(0);
    }, []);

    const thisOnSubmit = useCallback(
        (data: any) => {
            setResult(data);
            nextStep();
        },
        [nextStep],
    );

    const onSubmitted = useCallback(
        async (data: PardotSubmitProps) => {
            let thisData = {
                email: (data as PardotSubmitProps).email,
                firstName: (data as PardotSubmitProps).first_name,
                lastName: (data as PardotSubmitProps).last_name,
            };

            setIsSubmitted(true);
            try {
                await onSubmit({
                    ...resultCalculator,
                    ...thisData,
                });
            } catch (e) {
                console.error(e);
            }
        },
        [onSubmit, resultCalculator],
    );

    const onResult = (data) => {
        if (marker) setMarker(null);

        const { location, country, city, line_1 } = data;

        if (location) {
            const { latitude, longitude } = location;
            setMarker({
                line_1,
                city,
                country,
                latitude,
                longitude,
            });

            nextStep();
        } else {
            setMarker(null);
        }
    };

    const pin = useMemo(
        () =>
            marker ? (
                <Marker
                    longitude={marker.longitude}
                    latitude={marker.latitude}
                    anchor="bottom">
                    <Pin />
                </Marker>
            ) : null,
        [marker],
    );

    const onMount = (e) => {
        const input = e?._inputEl;

        if (step !== 0) {
            return;
        }

        input?.focus();
    };

    const onClickStepBack = useCallback(
        (step) => {
            if (step === 1) {
                geocoderRef.current.clear();
                return;
            }

            if (step === 2) {
                setIsSubmitted(false);
            }

            previousStep();
        },
        [previousStep],
    );

    return (
        <section className="before:!content-none mb-lg">
            <div className={cls('flex flex-col lg:flex-row')}>
                <FormProvider {...methods}>
                    <div className="order-last lg:order-first z-10 lg:absolute lg:inset-0 lg:z-10 lg:pointer-events-none ">
                        <div className="container relative row rounded-t-large lg:rounded-none overflow-hidden bg-background-primary lg:bg-transparent">
                            <div className="absolute top-0 left-0 right-0 w-full lg:hidden z-20">
                                <ProgressBar
                                    step={step}
                                    completed={isSubmitted}
                                />
                            </div>
                            <div
                                className={cls(
                                    'col-span-full lg:col-span-5 xl:col-span-8 lg:overflow-auto lg:max-h-screen scrollbar-hide',
                                )}>
                                <div className="lg:bg-background-primary lg:rounded-large lg:pointer-events-auto lg:relative lg:mt-[calc(104px+64px)] xl:mt-[calc(112px+64px)] lg:mb-32 lg:overflow-hidden">
                                    <div className="absolute top-0 left-0 right-0 w-full hidden lg:block z-20">
                                        <ProgressBar
                                            step={step}
                                            completed={isSubmitted}
                                        />
                                    </div>
                                    <div className={cls('py-xg lg:p-xg')}>
                                        <StepsContent
                                            labelSteps={
                                                translations[
                                                    'calculator.form.steps.text'
                                                ]?.text
                                            }
                                            step={step}
                                            translations={translations}
                                            title={title}
                                            description={description}
                                            calculatorTitle={calculatorTitle}
                                            calculatorDescription={
                                                calculatorDescription
                                            }
                                            formTitle={formTitle}
                                            formDescription={formDescription}
                                            submitTitle={submitTitle}
                                            submitDescription={
                                                submitDescription
                                            }
                                            caption={caption}
                                            benefits={benefits}
                                            marker={marker}
                                            onSubmit={thisOnSubmit}
                                            isSubmitted={isSubmitted}
                                            form={form}
                                            onSubmitted={onSubmitted}
                                            backButton={backButton}
                                            resultCalculator={resultCalculator}
                                            onClickStepBack={onClickStepBack}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <aside
                        className={cls(
                            'relative h-full w-full min-h-[calc(35vh+96px)] overflow-hidden -mb-xg lg:mb-0 lg:min-w-full lg:h-screen',
                        )}>
                        <div className={cls('absolute inset-0 h-full w-full')}>
                            <Map
                                initialViewState={DEFAULT_MAP}
                                id="map"
                                mapStyle={MAPBOX_STYLES}
                                mapboxAccessToken={mapToken}>
                                <Geocoder
                                    mapboxAccessToken={mapToken}
                                    onResult={onResult}
                                    ref={geocoderRef}
                                    onClear={resetSteps}
                                    containerNode={'geocoder'}
                                    placeholder={
                                        translations[
                                            'calculator.map.input.placeholder'
                                        ]?.text
                                    }
                                    onMount={onMount}
                                />
                                {pin}
                                {marker && (
                                    <Popup
                                        anchor="bottom"
                                        closeButton={false}
                                        offset={56}
                                        longitude={Number(marker.longitude)}
                                        latitude={Number(marker.latitude)}>
                                        <Title
                                            className={cls(
                                                '!text-b400 text-text-secondary',
                                            )}
                                            component="p">
                                            {marker?.country}
                                        </Title>
                                        <Text
                                            component="div"
                                            className={cls(
                                                '!text-b400 text-text-invert',
                                            )}>
                                            {[marker?.line_1, marker?.city]
                                                .filter((l) => l)
                                                .join(', ')}
                                        </Text>
                                    </Popup>
                                )}
                            </Map>
                        </div>
                    </aside>
                </FormProvider>
            </div>
        </section>
    );
};

export default CalculatorSection;
